import { useMutation } from '@tanstack/react-query';
import {
	transitionAuthScope,
	SCOPE_TRANSITION_STATUS,
} from '@/features/auth/session/transitionAuthScope';
import { normalizeScope } from '@/features/auth/domain/session/scope';
import { sessionStore } from '@/features/auth/domain/session/sessionStore';
import { clearAllStoresAndLocalStorage } from '@/legacy/utils';

export type FakeAccessVars = {
	companyId: number;
	companyName: string;
	delegationsIds?: number[];
	navigateOnSuccess?: boolean;
};

type FakeAccessResult = { activated: boolean };

export type FakeAccessMutationCallbacks = {
	onSuccess?: (data: FakeAccessResult, vars: FakeAccessVars) => void;
	onError?: (error: unknown, vars: FakeAccessVars) => void;
};

// Callbacks are wired into useMutation's own config (not passed to mutate()),
// so they always fire once the request settles — mutate()-level callbacks are
// dropped by TanStack Query when the calling component has since unmounted.
//
// Delegates the endpoint choice (impersonate vs. switch-credentials, by role) and
// the scoped credential cache commit entirely to transitionAuthScope. This hook
// only normalizes the target scope and adapts its result into a mutation outcome.
export const useFakeAccessMutation = (callbacks?: FakeAccessMutationCallbacks) => {
	return useMutation({
		mutationKey: ['auth', 'fakeAccess'],
		mutationFn: async (vars: FakeAccessVars): Promise<FakeAccessResult> => {
			const scope = normalizeScope(vars.companyId, vars.delegationsIds ?? []);
			if (!scope) throw new Error('Invalid company/delegation scope for fake access');

			const result = await transitionAuthScope(scope);
			if (result.status === SCOPE_TRANSITION_STATUS.FAILED) {
				throw result.error ?? new Error('Failed to activate company scope');
			}
			return { activated: result.status !== SCOPE_TRANSITION_STATUS.STALE };
		},
		onSuccess: (data, vars) => {
			// A STALE result means this transition was superseded by a newer one
			// before it could activate — the superseding transition already owns
			// the store reset and navigation, so running these side effects here
			// would wipe/persist/navigate for a switch that never took effect.
			if (!data.activated) return;

			// Wipes stale Zustand/table-filter state left over from the previous
			// company. clearAllStoresAndLocalStorage() wipes localStorage wholesale
			// (see src/legacy/utils/clearStoreAndStorage.ts), which would otherwise
			// erase the scoped session transitionAuthScope just persisted above —
			// persistToStorage() re-writes the current (already-correct) in-memory
			// state back out afterwards.
			clearAllStoresAndLocalStorage();
			sessionStore.getState().persistToStorage();
			callbacks?.onSuccess?.(data, vars);
		},
		onError: (error, vars) => {
			callbacks?.onError?.(error, vars);
		},
	});
};
